import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  Target,
  TrendingUp,
  Shield,
  Gauge,
  DollarSign,
  Loader2,
  X
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";

const KIValueCheck = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Set default message based on analysis when results are shown
  const getDefaultMessage = () => {
    const analysis = getAnalysis();
    if (currentLanguage === 'de') {
      return `Ich habe den KI-Value-Check durchgeführt und einen Reifegrad von ${analysis.score.percentage}% erreicht. Ich interessiere mich für ein Executive Briefing, um meine KI-Roadmap zu validieren und konkrete nächste Schritte zu besprechen.`;
    }
    return `I completed the AI Value Check and achieved a maturity score of ${analysis.score.percentage}%. I'm interested in an Executive Briefing to validate my AI roadmap and discuss concrete next steps.`;
  };

  const seoData = {
    title: currentLanguage === 'de' 
      ? 'KI-Value-Check - Testen Sie Ihren KI-Reifegrad | BROVKO'
      : 'AI Value Check - Test Your AI Maturity | BROVKO',
    description: currentLanguage === 'de'
      ? 'Ermitteln Sie in 3 Minuten den Reifegrad Ihrer KI-Initiativen und erhalten Sie eine personalisierte Analyse.'
      : 'Determine the maturity level of your AI initiatives in 3 minutes and receive a personalized analysis.',
    keywords: 'KI-Value-Check, AI Maturity Assessment, KI Reifegrad',
    canonicalUrl: '/ki-value-check'
  };

  const questions = [
    {
      id: 'maturity',
      title: currentLanguage === 'de' 
        ? 'Wie viele KI-Pilotprojekte laufen aktuell in Ihrem Unternehmen?'
        : 'How many AI pilot projects are currently running in your company?',
      options: [
        { value: '0', label: '0', score: 1 },
        { value: '1-5', label: '1-5', score: 2 },
        { value: '>5', label: '>5', score: 3 }
      ]
    },
    {
      id: 'measurability',
      title: currentLanguage === 'de'
        ? 'Können Sie den aktuellen EBIT-Impact Ihrer KI-Initiativen auf Knopfdruck beziffern?'
        : 'Can you quantify the current EBIT impact of your AI initiatives at the push of a button?',
      options: [
        { value: 'yes', label: currentLanguage === 'de' ? 'Ja' : 'Yes', score: 3 },
        { value: 'partial', label: currentLanguage === 'de' ? 'Teilweise' : 'Partially', score: 2 },
        { value: 'no', label: currentLanguage === 'de' ? 'Nein' : 'No', score: 1 }
      ]
    },
    {
      id: 'sovereignty',
      title: currentLanguage === 'de'
        ? 'Wo liegen Ihre KI-Daten aktuell?'
        : 'Where is your AI data currently stored?',
      options: [
        { value: 'us-cloud', label: currentLanguage === 'de' ? 'US-Cloud' : 'US Cloud', score: 1 },
        { value: 'hybrid', label: 'Hybrid', score: 2 },
        { value: 'sovereign', label: currentLanguage === 'de' ? 'Souveräne DE-Cloud / STACKIT' : 'Sovereign DE Cloud / STACKIT', score: 3 }
      ]
    },
    {
      id: 'governance',
      title: currentLanguage === 'de'
        ? 'Ist Ihr Unternehmen bereits auf die Anforderungen des EU AI Act vorbereitet?'
        : 'Is your company already prepared for the requirements of the EU AI Act?',
      options: [
        { value: 'complete', label: currentLanguage === 'de' ? 'Vollständig' : 'Completely', score: 3 },
        { value: 'in-progress', label: currentLanguage === 'de' ? 'In Arbeit' : 'In Progress', score: 2 },
        { value: 'none', label: currentLanguage === 'de' ? 'Bisher kein Fokus' : 'No focus yet', score: 1 }
      ]
    },
    {
      id: 'scaling',
      title: currentLanguage === 'de'
        ? 'Was ist die größte Barriere für den Roll-out?'
        : 'What is the biggest barrier to roll-out?',
      options: [
        { value: 'tech', label: currentLanguage === 'de' ? 'Technik' : 'Technology', score: 2 },
        { value: 'roi', label: currentLanguage === 'de' ? 'Kosten-Nutzen-Nachweis' : 'Cost-benefit proof', score: 1 },
        { value: 'strategy', label: currentLanguage === 'de' ? 'Fehlende Strategie' : 'Missing strategy', score: 1 }
      ]
    },
    {
      id: 'budget',
      title: currentLanguage === 'de'
        ? 'Wurde für 2026 ein dezidiertes KI-Budget für die Skalierung definiert?'
        : 'Has a dedicated AI budget for scaling been defined for 2026?',
      options: [
        { value: 'yes', label: currentLanguage === 'de' ? 'Ja' : 'Yes', score: 3 },
        { value: 'planning', label: currentLanguage === 'de' ? 'In Planung' : 'In planning', score: 2 },
        { value: 'no', label: currentLanguage === 'de' ? 'Nein' : 'No', score: 1 }
      ]
    },
    {
      id: 'goal',
      title: currentLanguage === 'de'
        ? 'Welches Ziel hat oberste Priorität?'
        : 'Which goal has the highest priority?',
      options: [
        { value: 'cost', label: currentLanguage === 'de' ? 'Kostensenkung' : 'Cost reduction', score: 2 },
        { value: 'revenue', label: currentLanguage === 'de' ? 'Umsatzsteigerung' : 'Revenue increase', score: 2 },
        { value: 'competitive', label: currentLanguage === 'de' ? 'Wettbewerbsvorsprung' : 'Competitive advantage', score: 3 }
      ]
    }
  ];

  const calculateScore = () => {
    let totalScore = 0;
    let maxScore = questions.length * 3;
    
    Object.values(answers).forEach(answer => {
      totalScore += answer.score;
    });

    return {
      total: totalScore,
      max: maxScore,
      percentage: Math.round((totalScore / maxScore) * 100)
    };
  };

  const getAnalysis = () => {
    const score = calculateScore();
    const measurability = answers.measurability?.value;
    const governance = answers.governance?.value;
    const budget = answers.budget?.value;
    
    let headline = '';
    let recommendation = '';
    let focusAreas = [];

    if (score.percentage < 40) {
      headline = currentLanguage === 'de' 
        ? 'Ihre KI-Reise beginnt – mit der richtigen Grundlage'
        : 'Your AI journey begins – with the right foundation';
      recommendation = currentLanguage === 'de'
        ? 'Sie haben gute Voraussetzungen, aber die strukturierte Basis fehlt noch. Wir empfehlen ein Executive Briefing, um die Value-Engine aufzusetzen.'
        : 'You have good prerequisites, but the structured foundation is still missing. We recommend an executive briefing to set up the value engine.';
      focusAreas = [
        currentLanguage === 'de' ? 'Portfolio-Aufbau' : 'Portfolio Setup',
        currentLanguage === 'de' ? 'KPI-Framework' : 'KPI Framework',
        currentLanguage === 'de' ? 'Governance-Basics' : 'Governance Basics'
      ];
    } else if (score.percentage < 70) {
      headline = currentLanguage === 'de'
        ? 'Starke Piloten – aber die EBIT-Messbarkeit ist Ihr Flaschenhals'
        : 'Strong pilots – but EBIT measurability is your bottleneck';
      recommendation = currentLanguage === 'de'
        ? 'Sie haben bereits KI-Erfahrung gesammelt. Jetzt geht es darum, von Piloten zur skalierbaren Value-Engine zu wechseln.'
        : 'You have already gained AI experience. Now it\'s about moving from pilots to a scalable value engine.';
      focusAreas = [
        currentLanguage === 'de' ? 'Value-Dashboard' : 'Value Dashboard',
        currentLanguage === 'de' ? 'Skalierungsstrategie' : 'Scaling Strategy',
        currentLanguage === 'de' ? 'Operating Rhythm' : 'Operating Rhythm'
      ];
    } else {
      headline = currentLanguage === 'de'
        ? 'Fortgeschrittene Position – optimieren Sie Ihre Value-Engine'
        : 'Advanced position – optimize your value engine';
      recommendation = currentLanguage === 'de'
        ? 'Sie sind bereits gut aufgestellt. Im Executive Briefing identifizieren wir Optimierungspotenziale für noch mehr EBIT-Impact.'
        : 'You are already well positioned. In the executive briefing, we identify optimization potential for even more EBIT impact.';
      focusAreas = [
        currentLanguage === 'de' ? 'Skalierung' : 'Scaling',
        currentLanguage === 'de' ? 'Benchmark-Vergleich' : 'Benchmark Comparison',
        currentLanguage === 'de' ? 'Best Practices' : 'Best Practices'
      ];
    }

    return { headline, recommendation, focusAreas, score };
  };

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Show calculating animation
      setIsCalculating(true);
      setTimeout(() => {
        setIsCalculating(false);
        setShowResults(true);
      }, 2000);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/value-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          answers,
          analysis: getAnalysis(),
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
      }
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestion = questions[currentStep];
  const isAnswered = answers[currentQuestion?.id];

  // Calculating Screen
  if (isCalculating) {
    return (
      <div className="theme-brand bg-surface text-ink min-h-screen bg-brand-hero flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="absolute inset-0 border-4 border-teal-500/30 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-brand rounded-full border-t-transparent animate-spin"></div>
          </div>
          <h2 className="text-[1.6rem] font-extrabold tracking-[-0.025em] leading-snug text-white mb-2">
            {currentLanguage === 'de' ? 'Ihre KI-Value-Map wird berechnet...' : 'Calculating your AI Value Map...'}
          </h2>
          <p className="text-ink-faint">
            {currentLanguage === 'de' ? 'Analysiere Ihre Antworten' : 'Analyzing your answers'}
          </p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (showResults) {
    const analysis = getAnalysis();
    
    return (
      <div className="min-h-screen bg-surface-sunken">
        <SEOHead {...seoData} />
        <Header />
        
        <div className="pt-36 pb-16">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Results Header */}
            <div className="max-w-[64ch] mb-12">
              <div className="inline-flex items-center gap-2 bg-brand/10 text-brand-300 border border-brand-400/25 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <CheckCircle className="h-4 w-4" />
                {currentLanguage === 'de' ? 'Analyse abgeschlossen' : 'Analysis complete'}
              </div>
              <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-4">
                {analysis.headline}
              </h1>
              <p className="text-lg text-ink-muted max-w-[62ch]">
                {analysis.recommendation}
              </p>
            </div>

            {/* Score Card */}
            <div className="bg-surface rounded-2xl shadow-xl p-8 mb-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Left - Score */}
                <div className="text-center md:text-left">
                  <p className="text-ink-faint text-sm mb-2">
                    {currentLanguage === 'de' ? 'Ihr KI-Reifegrad' : 'Your AI Maturity'}
                  </p>
                  <div className="flex items-end gap-2 justify-center md:justify-start">
                    <span className="text-6xl font-bold text-ink">{analysis.score.percentage}</span>
                    <span className="text-2xl text-ink-faint mb-2">%</span>
                  </div>
                  <div className="w-full bg-white/[0.09] rounded-full h-3 mt-4">
                    <div 
                      className="bg-gradient-to-r from-brand to-cyan-500 h-3 rounded-full transition-all duration-1000"
                      style={{ width: `${analysis.score.percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Right - Focus Areas */}
                <div>
                  <p className="text-ink-faint text-sm mb-4">
                    {currentLanguage === 'de' ? 'Fokus-Bereiche' : 'Focus Areas'}
                  </p>
                  <div className="space-y-3">
                    {analysis.focusAreas.map((area, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-brand/15 rounded-lg flex items-center justify-center">
                          <Target className="h-4 w-4 text-brand-400" />
                        </div>
                        <span className="text-ink-muted font-medium">{area}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Value Map - Detailed Results */}
            <div className="bg-surface rounded-2xl shadow-xl p-8 mb-8">
              <h3 className="text-xl font-bold text-ink mb-6">
                {currentLanguage === 'de' ? 'Ihre Value-Map im Detail' : 'Your Value Map in Detail'}
              </h3>
              
              <div className="space-y-6">
                {/* Maturity */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-muted font-medium">
                      {currentLanguage === 'de' ? 'Reifegrad (Pilotprojekte)' : 'Maturity (Pilot Projects)'}
                    </span>
                    <span className="text-brand-400 font-semibold text-sm bg-brand/10 px-3 py-1 rounded-full">
                      {answers.maturity?.label || '–'}
                    </span>
                  </div>
                  <div className="h-3 bg-white/[0.09] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-brand to-teal-400 rounded-full transition-all duration-700"
                      style={{ width: `${(answers.maturity?.score || 0) * 33.3}%` }}
                    ></div>
                  </div>
                </div>

                {/* Measurability */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-muted font-medium">
                      {currentLanguage === 'de' ? 'Messbarkeit (EBIT-Impact)' : 'Measurability (EBIT Impact)'}
                    </span>
                    <span className="text-brand-400 font-semibold text-sm bg-brand/10 px-3 py-1 rounded-full">
                      {answers.measurability?.label || '–'}
                    </span>
                  </div>
                  <div className="h-3 bg-white/[0.09] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-brand-cyan rounded-full transition-all duration-700"
                      style={{ width: `${(answers.measurability?.score || 0) * 33.3}%` }}
                    ></div>
                  </div>
                </div>

                {/* Sovereignty */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-muted font-medium">
                      {currentLanguage === 'de' ? 'Souveränität (Daten-Standort)' : 'Sovereignty (Data Location)'}
                    </span>
                    <span className="text-purple-400 font-semibold text-sm bg-purple-500/10 px-3 py-1 rounded-full">
                      {answers.sovereignty?.label || '–'}
                    </span>
                  </div>
                  <div className="h-3 bg-white/[0.09] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full transition-all duration-700"
                      style={{ width: `${(answers.sovereignty?.score || 0) * 33.3}%` }}
                    ></div>
                  </div>
                </div>

                {/* Governance */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-muted font-medium">
                      {currentLanguage === 'de' ? 'Governance (EU AI Act)' : 'Governance (EU AI Act)'}
                    </span>
                    <span className="text-blue-400 font-semibold text-sm bg-blue-500/10 px-3 py-1 rounded-full">
                      {answers.governance?.label || '–'}
                    </span>
                  </div>
                  <div className="h-3 bg-white/[0.09] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-700"
                      style={{ width: `${(answers.governance?.score || 0) * 33.3}%` }}
                    ></div>
                  </div>
                </div>

                {/* Scaling */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-muted font-medium">
                      {currentLanguage === 'de' ? 'Skalierung (Barrieren)' : 'Scaling (Barriers)'}
                    </span>
                    <span className="text-orange-400 font-semibold text-sm bg-orange-500/10 px-3 py-1 rounded-full">
                      {answers.scaling?.label || '–'}
                    </span>
                  </div>
                  <div className="h-3 bg-white/[0.09] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all duration-700"
                      style={{ width: `${(answers.scaling?.score || 0) * 33.3}%` }}
                    ></div>
                  </div>
                </div>

                {/* Budget */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-muted font-medium">
                      {currentLanguage === 'de' ? 'Budget (2026)' : 'Budget (2026)'}
                    </span>
                    <span className="text-green-400 font-semibold text-sm bg-green-500/10 px-3 py-1 rounded-full">
                      {answers.budget?.label || '–'}
                    </span>
                  </div>
                  <div className="h-3 bg-white/[0.09] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-700"
                      style={{ width: `${(answers.budget?.score || 0) * 33.3}%` }}
                    ></div>
                  </div>
                </div>

                {/* Goal */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-ink-muted font-medium">
                      {currentLanguage === 'de' ? 'Priorität (Ziel)' : 'Priority (Goal)'}
                    </span>
                    <span className="text-indigo-400 font-semibold text-sm bg-indigo-500/10 px-3 py-1 rounded-full">
                      {answers.goal?.label || '–'}
                    </span>
                  </div>
                  <div className="h-3 bg-white/[0.09] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all duration-700"
                      style={{ width: `${(answers.goal?.score || 0) * 33.3}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Form */}
            <div className="bg-surface rounded-2xl p-8">
              <div className="max-w-[64ch] mb-8">
                <h2 className="text-[1.6rem] font-extrabold tracking-[-0.025em] leading-snug text-white mb-2">
                  {currentLanguage === 'de' 
                    ? 'Validieren Sie Ihre Roadmap im Executive Briefing'
                    : 'Validate your roadmap in an Executive Briefing'
                  }
                </h2>
                <p className="text-ink-faint">
                  {currentLanguage === 'de'
                    ? '60 Minuten • Kostenlos • Personalisiert'
                    : '60 Minutes • Free • Personalized'
                  }
                </p>
              </div>

              {submitSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">
                    {currentLanguage === 'de' ? 'Anfrage gesendet!' : 'Request sent!'}
                  </h3>
                  <p className="text-ink-faint">
                    {currentLanguage === 'de' 
                      ? 'Wir melden uns innerhalb von 24 Stunden bei Ihnen.'
                      : 'We will contact you within 24 hours.'
                    }
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-4">
                  <p className="text-ink-faint text-sm mb-4">
                    {currentLanguage === 'de' ? '* Pflichtfelder' : '* Required fields'}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-ink-muted text-sm mb-1">
                        {currentLanguage === 'de' ? 'Vorname' : 'First Name'} <span className="text-red-400">*</span>
                      </label>
                      <Input
                        placeholder={currentLanguage === 'de' ? 'Ihr Vorname' : 'Your first name'}
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        required
                        className="bg-white/[0.05] border-hairline-strong text-white placeholder:text-ink-faint"
                        data-testid="value-check-firstname"
                      />
                    </div>
                    <div>
                      <label className="block text-ink-muted text-sm mb-1">
                        {currentLanguage === 'de' ? 'Nachname' : 'Last Name'} <span className="text-red-400">*</span>
                      </label>
                      <Input
                        placeholder={currentLanguage === 'de' ? 'Ihr Nachname' : 'Your last name'}
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        required
                        className="bg-white/[0.05] border-hairline-strong text-white placeholder:text-ink-faint"
                        data-testid="value-check-lastname"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-ink-muted text-sm mb-1">
                      {currentLanguage === 'de' ? 'E-Mail' : 'Email'} <span className="text-red-400">*</span>
                    </label>
                    <Input
                      type="email"
                      placeholder={currentLanguage === 'de' ? 'ihre@email.de' : 'your@email.com'}
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="bg-white/[0.05] border-hairline-strong text-white placeholder:text-ink-faint"
                      data-testid="value-check-email"
                    />
                  </div>
                  <div>
                    <label className="block text-ink-muted text-sm mb-1">
                      {currentLanguage === 'de' ? 'Telefonnummer' : 'Phone Number'}
                    </label>
                    <Input
                      type="tel"
                      placeholder={currentLanguage === 'de' ? '+49 ...' : '+49 ...'}
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="bg-white/[0.05] border-hairline-strong text-white placeholder:text-ink-faint"
                      data-testid="value-check-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-ink-muted text-sm mb-1">
                      {currentLanguage === 'de' ? 'Ihre Nachricht' : 'Your Message'}
                    </label>
                    <Textarea
                      placeholder={currentLanguage === 'de' ? 'Ihre Nachricht...' : 'Your message...'}
                      value={formData.message || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      rows={4}
                      className="bg-white/[0.05] border-hairline-strong text-white placeholder:text-ink-faint resize-none"
                      data-testid="value-check-message"
                    />
                  </div>
                  <Button 
                    type="submit"
                    disabled={isSubmitting || !formData.firstName || !formData.lastName || !formData.email}
                    className="w-full bg-brand hover:bg-brand-600 text-ink-onBrand py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="value-check-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {currentLanguage === 'de' ? 'Wird gesendet...' : 'Sending...'}
                      </>
                    ) : (
                      currentLanguage === 'de' ? 'Termin anfragen' : 'Request appointment'
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Question Screen
  return (
    <div className="min-h-screen bg-surface-sunken">
      <SEOHead {...seoData} />
      <Header />
      
      <div className="pt-44 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            {/* Left - Question */}
            <div className="max-w-xl bg-brand-hero rounded-xl p-6 shadow-xl">
              {/* Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-ink-faint mb-1">
                  <span>{currentLanguage === 'de' ? `Frage ${currentStep + 1} von ${questions.length}` : `Question ${currentStep + 1} of ${questions.length}`}</span>
                  <span>{Math.round(((currentStep + 1) / questions.length) * 100)}%</span>
                </div>
                <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-brand-accent transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Question */}
              <h2 className="text-xl sm:text-[1.6rem] font-extrabold tracking-[-0.025em] leading-snug text-white mb-5">
                {currentQuestion?.title}
              </h2>

              {/* Options */}
              <div className="space-y-2.5 mb-5">
                {currentQuestion?.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(currentQuestion.id, option)}
                    className={`w-full text-left p-3.5 rounded-lg border-2 transition-all duration-200 ${
                      answers[currentQuestion.id]?.value === option.value
                        ? 'bg-brand/15 border-brand text-white'
                        : 'bg-surface-raised/50 border-hairline text-ink-muted hover:border-hairline-strong'
                    }`}
                  >
                    <span className="text-base font-medium">{option.label}</span>
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-3">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="border-hairline-strong text-white hover:bg-white/[0.07] h-10"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {currentLanguage === 'de' ? 'Zurück' : 'Back'}
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={!isAnswered}
                  className="flex-1 bg-brand hover:bg-brand-600 text-ink-onBrand h-10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentStep === questions.length - 1 
                    ? (currentLanguage === 'de' ? 'Analyse starten' : 'Start analysis')
                    : (currentLanguage === 'de' ? 'Weiter' : 'Next')
                  }
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Right - Value Map Visualization */}
            <div className="hidden lg:block">
              <div className="bg-brand-hero rounded-xl p-6 shadow-xl">
                <h3 className="text-white font-semibold mb-4 text-sm">
                  {currentLanguage === 'de' ? 'Ihre Value-Map' : 'Your Value Map'}
                </h3>
                
                {/* Simple Score Indicators */}
                <div className="space-y-2.5">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-ink-faint">{currentLanguage === 'de' ? 'Reifegrad' : 'Maturity'}</span>
                      <span className="text-brand-400">{answers.maturity ? '✓' : '–'}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-brand transition-all duration-500"
                        style={{ width: answers.maturity ? `${answers.maturity.score * 33}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-ink-faint">{currentLanguage === 'de' ? 'Messbarkeit' : 'Measurability'}</span>
                      <span className="text-brand-400">{answers.measurability ? '✓' : '–'}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-cyan-500 transition-all duration-500"
                        style={{ width: answers.measurability ? `${answers.measurability.score * 33}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-ink-faint">{currentLanguage === 'de' ? 'Souveränität' : 'Sovereignty'}</span>
                      <span className="text-brand-400">{answers.sovereignty ? '✓' : '–'}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 transition-all duration-500"
                        style={{ width: answers.sovereignty ? `${answers.sovereignty.score * 33}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-ink-faint">{currentLanguage === 'de' ? 'Governance' : 'Governance'}</span>
                      <span className="text-brand-400">{answers.governance ? '✓' : '–'}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all duration-500"
                        style={{ width: answers.governance ? `${answers.governance.score * 33}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-ink-faint">{currentLanguage === 'de' ? 'Skalierung' : 'Scaling'}</span>
                      <span className="text-brand-400">{answers.scaling ? '✓' : '–'}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 transition-all duration-500"
                        style={{ width: answers.scaling ? `${answers.scaling.score * 33}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-ink-faint">{currentLanguage === 'de' ? 'Budget' : 'Budget'}</span>
                      <span className="text-brand-400">{answers.budget ? '✓' : '–'}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all duration-500"
                        style={{ width: answers.budget ? `${answers.budget.score * 33}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-ink-faint">{currentLanguage === 'de' ? 'Ziel' : 'Goal'}</span>
                      <span className="text-brand-400">{answers.goal ? '✓' : '–'}</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.07] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-indigo-500 transition-all duration-500"
                        style={{ width: answers.goal ? `${answers.goal.score * 33}%` : '0%' }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Teaser for final score */}
                <div className="mt-5 pt-4 border-t border-hairline">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center">
                      <Gauge className="h-6 w-6 text-brand-400" />
                    </div>
                    <p className="text-ink-muted text-xs leading-relaxed">
                      {currentLanguage === 'de' 
                        ? 'Nach der letzten Frage erhalten Sie Ihren persönlichen KI-Reifegrad-Score.'
                        : 'After the last question, you will receive your personal AI maturity score.'
                      }
                    </p>
                    <p className="text-brand-400 text-xs mt-2 font-medium">
                      {currentLanguage === 'de' 
                        ? `Noch ${questions.length - Object.keys(answers).length} Fragen`
                        : `${questions.length - Object.keys(answers).length} questions remaining`
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default KIValueCheck;
